import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useSearchParams, Link } from 'react-router-dom';
import { FiArrowLeft, FiSearch, FiFilter, FiX } from 'react-icons/fi';
import { getCourses } from '../services/api';
import { CourseCard } from '../components/courses';

const Container = styled.div`
  padding: 80px 20px;
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  margin-bottom: 3rem;
`;

const BackButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: ${props => props.theme.colors.primary};
  text-decoration: none;
  font-weight: 500;
  margin-bottom: 1rem;
  transition: color 0.3s ease;

  &:hover {
    color: ${props => props.theme.colors.primaryDark};
  }
`;

const Title = styled.h1`
  font-size: 2.5rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1rem;
  font-weight: 700;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const SearchInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
`;

const SearchQuery = styled.div`
  background: ${props => props.theme.colors.primary}20;
  color: ${props => props.theme.colors.primary};
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const FilterTag = styled.div`
  background: ${props => props.theme.colors.secondary}20;
  color: ${props => props.theme.colors.secondary};
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ResultsCount = styled.p`
  color: ${props => props.theme.colors.textSecondary};
  font-size: 1.1rem;
  margin-bottom: 2rem;
`;

const CoursesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 2rem;
  margin-top: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  font-size: 1.2rem;
  color: ${props => props.theme.colors.textSecondary};
`;

const ErrorContainer = styled.div`
  text-align: center;
  padding: 2rem;
  color: ${props => props.theme.colors.error};
  font-size: 1.1rem;
`;

const NoResultsContainer = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: ${props => props.theme.colors.textSecondary};
`;

const NoResultsIcon = styled.div`
  font-size: 4rem;
  color: ${props => props.theme.colors.textLight};
  margin-bottom: 1rem;
`;

const NoResultsTitle = styled.h3`
  font-size: 1.5rem;
  color: ${props => props.theme.colors.text};
  margin-bottom: 1rem;
`;

const NoResultsText = styled.p`
  font-size: 1.1rem;
  margin-bottom: 2rem;
  max-width: 500px;
  margin-left: auto;
  margin-right: auto;
`;

const ClearFiltersButton = styled.button`
  background: ${props => props.theme.colors.primary};
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 25px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: ${props => props.theme.colors.primaryDark};
    transform: translateY(-1px);
  }
`;

const SearchResultsPage = () => {
  const [searchParams] = useSearchParams();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const searchQuery = searchParams.get('search');
  const category = searchParams.get('category');
  const level = searchParams.get('level');

  useEffect(() => {
    const fetchSearchResults = async () => {
      try {
        setLoading(true);
        setError(null);

        const params = {};
        if (searchQuery) params.search = searchQuery;
        if (category) params.category = category;
        if (level) params.level = level;
        params.limit = 1000; // Get all results for search

        const response = await getCourses(params);
        setCourses(response.courses || response);
      } catch (err) {
        console.error('Error fetching search results:', err);
        setError('Failed to load search results. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchSearchResults();
  }, [searchQuery, category, level]);

  const clearFilters = () => {
    window.location.href = '/courses';
  };

  if (loading) {
    return (
      <Container>
        <LoadingContainer>
          Searching for courses...
        </LoadingContainer>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <ErrorContainer>
          {error}
        </ErrorContainer>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <BackButton to="/">
          <FiArrowLeft />
          Back to Home
        </BackButton>
        
        <Title>Search Results</Title>
        
        <SearchInfo>
          {searchQuery && (
            <SearchQuery>
              <FiSearch />
              "{searchQuery}"
            </SearchQuery>
          )}
          
          {category && (
            <FilterTag>
              <FiFilter />
              Category: {category}
            </FilterTag>
          )}
          
          {level && (
            <FilterTag>
              <FiFilter />
              Level: {level}
            </FilterTag>
          )}
        </SearchInfo>
        
        <ResultsCount>
          Found {courses.length} {courses.length === 1 ? 'course' : 'courses'}
          {(searchQuery || category || level) && (
            <>
              {' '}matching your criteria
              <ClearFiltersButton onClick={clearFilters} style={{ marginLeft: '1rem' }}>
                <FiX />
                Clear Filters
              </ClearFiltersButton>
            </>
          )}
        </ResultsCount>
      </Header>

      {courses.length === 0 ? (
        <NoResultsContainer>
          <NoResultsIcon>
            <FiSearch />
          </NoResultsIcon>
          <NoResultsTitle>No courses found</NoResultsTitle>
          <NoResultsText>
            We couldn't find any courses matching your search criteria. 
            Try adjusting your search terms or browse our categories instead.
          </NoResultsText>
          <ClearFiltersButton onClick={clearFilters}>
            Browse All Categories
          </ClearFiltersButton>
        </NoResultsContainer>
      ) : (
        <CoursesGrid>
          {courses.map((course) => (
            <CourseCard
              key={course._id}
              course={course}
              categoryColor={course.category?.color || '#01675f'}
            />
          ))}
        </CoursesGrid>
      )}
    </Container>
  );
};

export default SearchResultsPage; 